import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Sparkles, FileText, Download, RefreshCw, ArrowLeft, Eye, ExternalLink, Trash2 } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { toast } from "sonner";
import {
 getBlogStats,
 listAdminArticlesCursor,
 generateArticlePlan,
 generateArticleDrafts,
 setArticleStatus,
 exportArticlesCsv,
 regenerateArticleMeta,
 bulkPublishEligible,
 bulkFillDraftIds,
 bulkSetArticleStatus,
 bulkDeleteArticles,
} from "@/lib/blog-admin-secure.functions";
import { AdminCursorTable } from "@/components/dashboard/AdminCursorTable";
import { ArticlePreviewDrawer } from "@/components/admin/ArticlePreviewDrawer";
import { AutoPilotStatus } from "@/components/admin/AutoPilotStatus";
import { Progress } from "@/components/ui/progress";

export const Route = createFileRoute("/dashboard/admin/blog")({
 component: AdminBlogPage,
});

function AdminBlogPage() {
 const { user, loading, primaryRole } = useAuth();
 const navigate = useNavigate();
 const callStats = useServerFn(getBlogStats);
 const callList = useServerFn(listAdminArticlesCursor);
 const callPlan = useServerFn(generateArticlePlan);
 const callDrafts = useServerFn(generateArticleDrafts);
 const callStatus = useServerFn(setArticleStatus);
 const callCsv = useServerFn(exportArticlesCsv);
 const callMeta = useServerFn(regenerateArticleMeta);
 const callBulkPublish = useServerFn(bulkPublishEligible);
 const callBulkFillIds = useServerFn(bulkFillDraftIds);
 const callBulkStatus = useServerFn(bulkSetArticleStatus);
 const callBulkDelete = useServerFn(bulkDeleteArticles);

 const [stats, setStats] = useState<any>(null);
 const [statusFilter, setStatusFilter] = useState<"all" | "draft" | "review" | "published" | "noindex">("all");
 const [search, setSearch] = useState("");
 const [appliedSearch, setAppliedSearch] = useState("");
 const [selected, setSelected] = useState<Record<string, boolean>>({});
 const [busy, setBusy] = useState(false);
 const [fillAllRunning, setFillAllRunning] = useState(false);
 const [publishRunning, setPublishRunning] = useState(false);
 const [previewId, setPreviewId] = useState<string | null>(null);
 const [progress, setProgress] = useState<{ label: string; done: number; total: number; detail?: string } | null>(null);
 // Bump to force the table to re-fetch the first page after a mutation.
 const [tableNonce, setTableNonce] = useState(0);

 const reloadStatsOnly = async () => {
 try { setStats(await callStats()); } catch {}
 };

 const reload = () => {
 reloadStatsOnly();
 setTableNonce((n) => n + 1);
 };

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 if (primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
 reloadStatsOnly();
 }, [user, loading, primaryRole]);


 // Live poll stats from DB every 8s so numbers stay fresh while long jobs run.
 useEffect(() => {
 if (!user || primaryRole !== "admin") return;
 const id = setInterval(() => { reloadStatsOnly(); }, 8000);
 return () => clearInterval(id);
 }, [user, primaryRole]);

 if (loading || !user || primaryRole !== "admin") {
 return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
 }

 const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

 const runPlan = async () => {
 if (!confirm("Seed the FULL 1,000,000,000-article plan? This runs in batches of 5,000 and will take a long time. You can leave the page; already-seeded rows are skipped on re-runs.")) return;
 setBusy(true);
 setProgress({ label: "Seeding plan", done: 0, total: 0 });
 try {
 let offset = 0;
 let total = 0;
 let inserted = 0;
 for (let i = 0; i < 200_000; i++) {
 const r: any = await callPlan({ data: { offset, limit: 5000 } });
 inserted += r.inserted ?? 0;
 total = r.total ?? 0;
 offset = r.nextOffset ?? offset;
 setProgress({ label: "Seeding plan", done: offset, total });
 if (i % 5 === 0) toast.message(`Seed progress: ${offset.toLocaleString()} / ${total.toLocaleString()}`);
 if (r.done || (r.planned ?? 0) === 0) break;
 }
 toast.success(`Seeded ${inserted.toLocaleString()} of ${total.toLocaleString()} articles`);
 await reload();
 } catch (e: any) { toast.error(e?.message ?? "Failed"); } finally { setBusy(false); setProgress(null); }
 };

 const runDrafts = async () => {
 if (selectedIds.length === 0) { toast.error("Select at least one article"); return; }
 setBusy(true);
 const ids = selectedIds.slice(0, 3);
 setProgress({ label: "Filling content", done: 0, total: ids.length });
 try {
 const r = await callDrafts({ data: { ids } });
 setProgress({ label: "Filling content", done: r.updated, total: ids.length });
 toast.success(`Filled ${r.updated} drafts${selectedIds.length > 3 ? " · select Fill ALL for larger batches" : ""}${r.errors.length ? ` · ${r.errors.length} errors` : ""}`);
 setSelected({});
 await reload();
 } catch (e: any) { toast.error(e?.message ?? "Failed"); } finally { setBusy(false); setProgress(null); }
 };

 const flip = async (id: string, status: "draft" | "review" | "published" | "noindex") => {
 try {
 await callStatus({ data: { id, status } });
 toast.success(`Marked ${status}`);
 await reload();
 } catch (e: any) { toast.error(e?.message ?? "Failed"); }
 };

 const regenMeta = async (id: string) => {
 try {
 await callMeta({ data: { id } });
 toast.success("Meta regenerated");
 await reload();
 } catch (e: any) { toast.error(e?.message ?? "Failed"); }
 };

 const runBulkStatus = async (status: "draft" | "review" | "published" | "noindex") => {
 if (selectedIds.length === 0) { toast.error("Select at least one article"); return; }
 if (!confirm(`Set ${selectedIds.length} article(s) to "${status}"?`)) return;
 setBusy(true);
 try {
 const r = await callBulkStatus({ data: { ids: selectedIds, status } });
 toast.success(`Updated ${r.updated} article(s) → ${status}`);
 setSelected({});
 await reload();
 } catch (e: any) { toast.error(e?.message ?? "Failed"); } finally { setBusy(false); }
 };

 const runBulkDelete = async () => {
 if (selectedIds.length === 0) { toast.error("Select at least one article"); return; }
 if (!confirm(`Permanently delete ${selectedIds.length} article(s)? This cannot be undone.`)) return;
 setBusy(true);
 try {
 const r = await callBulkDelete({ data: { ids: selectedIds } });
 toast.success(`Deleted ${r.deleted} article(s)`);
 setSelected({});
 await reload();
 } catch (e: any) { toast.error(e?.message ?? "Failed"); } finally { setBusy(false); }
 };

 const runBulkPublish = async () => {
 if (!confirm("Publish ALL articles with SEO score ≥ 60? This will set them to indexable.")) return;
 setPublishRunning(true);
 try {
 const r = await callBulkPublish({ data: { minScore: 60, limit: 2000 } });
 toast.success(`Published ${r.published} articles`);
 await reload();
 } catch (e: any) { toast.error(e?.message ?? "Failed"); } finally { setPublishRunning(false); }
 };

 const runFillAllDrafts = async () => {
 setFillAllRunning(true);
 setProgress({ label: "Fetching next batch", done: 0, total: 0 });
 try {
 const { ids } = await callBulkFillIds();
 if (ids.length === 0) { toast.info("No empty drafts found"); return; }
 if (!confirm(`Fill the next ${ids.length} empty draft(s)? Each one spends AI credits. Click cancel to stop.`)) return;
 toast.message(`Filling ${ids.length} drafts...`);
 let processed = 0;
 let filled = 0;
 let failed = 0;
 setProgress({ label: "Filling drafts", done: 0, total: ids.length });
 // Sequential, 1 retry only. Stop the whole batch on credits-exhausted
 // OR repeated DB timeouts — both indicate continuing will only waste
 // more money / hammer the DB.
 const queue = [...ids];
 const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
 let stopReason: string | null = null;
 const fillOne = async (id: string): Promise<{ ok: boolean; err?: string; stop?: string }> => {
 let lastErr = "";
 for (let attempt = 0; attempt < 2; attempt++) {
 try {
 const r = await callDrafts({ data: { ids: [id] } });
 if (r.updated > 0) return { ok: true };
 lastErr = r.errors?.[0] ?? "no_update";
 } catch (e: any) {
 lastErr = e?.message ?? "request_failed";
 }
 if (/402|Not enough credits|payment_required/i.test(lastErr)) {
 return { ok: false, err: lastErr, stop: "credits" };
 }
 if (/statement timeout|canceling statement/i.test(lastErr)) {
 return { ok: false, err: lastErr, stop: "db_timeout" };
 }
 // Don't retry validation/JSON errors — they'll just waste another AI call.
 if (/JSON|parse|validation|invalid/i.test(lastErr)) {
 return { ok: false, err: lastErr };
 }
 await sleep(800);
 }
 return { ok: false, err: lastErr };
 };
 while (queue.length && !stopReason) {
 const next = queue.shift()!;
 const r = await fillOne(next);
 if (r.ok) filled++;
 else {
 failed++;
 if (r.stop) {
 stopReason = r.stop;
 if (r.stop === "credits") toast.error("Lovable AI credits exhausted — top up in Workspace → Plans & Billing, then retry.", { duration: 10000 });
 else toast.error("Database is timing out — stopped batch to protect your data.", { duration: 8000 });
 break;
 }
 if (failed <= 3) toast.error(`Draft failed: ${r.err?.slice(0, 120)}`);
 }
 processed++;
 setProgress({
 label: "Filling drafts",
 done: processed,
 total: ids.length,
 detail: `${filled.toLocaleString()} filled${failed ? ` · ${failed.toLocaleString()} failed` : ""}`,
 });
 if (processed % 5 === 0) reloadStatsOnly();
 }
 if (stopReason) {
 toast.warning(`Stopped early. ${filled.toLocaleString()} filled, ${failed.toLocaleString()} failed.`);
 } else if (failed) toast.warning(`Finished: ${filled.toLocaleString()} filled, ${failed.toLocaleString()} failed`);
 else toast.success(`Filled ${filled.toLocaleString()} drafts`);
 await reload();
 } catch (e: any) { toast.error(e?.message ?? "Failed"); } finally { setFillAllRunning(false); setProgress(null); }
 };

 const downloadCsv = async () => {
 try {
 const r = await callCsv();
 const blob = new Blob([r.csv], { type: "text/csv" });
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url; a.download = `heallexa-blog-${Date.now()}.csv`; a.click();
 URL.revokeObjectURL(url);
 } catch (e: any) { toast.error(e?.message ?? "Failed"); }
 };

 return (
 <AdminPageShell
 title="Blog content engine"
 description="Plan, generate, score, and publish healthcare discovery articles."
 primaryAction={{ label: "Refresh", icon: RefreshCw, onClick: reload }}
 >
 <div className="space-y-8">
 {/* Stats */}
 <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
 <Stat label="Total" value={stats?.total ?? "—"} />
 <Stat label="Published" value={stats?.published ?? "—"} />
 <Stat label="Drafts" value={stats?.drafts ?? "—"} />
 <Stat label="Review" value={stats?.review ?? "—"} />
 <Stat label="Avg SEO score" value={stats?.averageScore != null ? `${stats.averageScore}/100` : "—"} />
 </section>

 {/* Auto-pilot status */}
 <AutoPilotStatus />

 {/* Actions */}
 <section className="flex flex-wrap gap-2 items-center">
 <Button onClick={runPlan} disabled={busy} size="sm"><Sparkles className="h-4 w-4" /> Seed plan (1,000,000,000)</Button>
 <Button onClick={runDrafts} disabled={busy || selectedIds.length === 0} size="sm" variant="default"><FileText className="h-4 w-4" /> Fill content ({selectedIds.length})</Button>
 <Button onClick={runFillAllDrafts} disabled={fillAllRunning} size="sm" variant="secondary">{fillAllRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Fill next batch (up to 50)</Button>
 <Button onClick={runBulkPublish} disabled={publishRunning} size="sm" variant="default">{publishRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Publish all eligible (≥60)</Button>
 <Button onClick={downloadCsv} disabled={busy} size="sm" variant="outline"><Download className="h-4 w-4" /> Export CSV</Button>
 <Button onClick={reload} size="sm" variant="ghost"><RefreshCw className="h-4 w-4" /></Button>
 </section>

 {/* Progress */}
 {progress && (
 <section className="card-surface-flat p-4 space-y-2">
 <div className="flex items-center justify-between text-sm">
 <span className="font-medium inline-flex items-center gap-2">
 <Loader2 className="h-4 w-4 animate-spin" />
 {progress.label}
 </span>
 <span className="text-muted-foreground tabular-nums">
 {progress.total > 0
 ? `${progress.done.toLocaleString()} / ${progress.total.toLocaleString()} (${Math.min(100, Math.round((progress.done / progress.total) * 100))}%)`
 : "Starting…"}
 </span>
 </div>
 <Progress value={progress.total > 0 ? Math.min(100, (progress.done / progress.total) * 100) : 0} />
 {progress.detail && <p className="text-xs text-muted-foreground">{progress.detail}</p>}
 </section>
 )}

 {/* Filters */}
 <section className="flex flex-wrap gap-2 items-center border-t border-border pt-4">
 <div className="flex gap-1">
 {(["all", "draft", "review", "published", "noindex"] as const).map((s) => (
 <Button key={s} variant={statusFilter === s ? "default" : "outline"} size="sm" onClick={() => setStatusFilter(s)}>{s}</Button>
 ))}
 </div>
 <Input
 placeholder="Search title..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 onKeyDown={(e) => { if (e.key === "Enter") setAppliedSearch(search); }}
 className="max-w-xs"
 />
 <Button size="sm" variant="outline" onClick={() => setAppliedSearch(search)}>Search</Button>
 <span className="text-xs text-muted-foreground ml-auto">{stats?.total != null ? `~${Number(stats.total).toLocaleString()} total` : ""}</span>
 </section>

 {/* Bulk action bar */}
 {selectedIds.length > 0 && (
 <section className="flex flex-wrap gap-2 items-center border border-primary/30 bg-primary/5 p-3 rounded-md">
 <span className="text-sm font-medium">{selectedIds.length} selected</span>
 <span className="text-xs text-muted-foreground">·</span>
 <Button size="sm" variant="default" disabled={busy} onClick={() => runBulkStatus("published")}>Publish</Button>
 <Button size="sm" variant="outline" disabled={busy} onClick={() => runBulkStatus("review")}>Mark review</Button>
 <Button size="sm" variant="outline" disabled={busy} onClick={() => runBulkStatus("draft")}>Set draft</Button>
 <Button size="sm" variant="outline" disabled={busy} onClick={() => runBulkStatus("noindex")}>Noindex</Button>
 <Button size="sm" variant="default" disabled={busy || selectedIds.length === 0} onClick={runDrafts}><FileText className="h-3 w-3" /> Fill content</Button>
 <Button size="sm" variant="destructive" disabled={busy} onClick={runBulkDelete}><Trash2 className="h-3 w-3" /> Delete</Button>
 <Button size="sm" variant="ghost" onClick={() => setSelected({})} className="ml-auto">Clear</Button>
 </section>
 )}

 {/* Table — cursor pagination, NEVER loads all rows. */}
 <AdminCursorTable<any, { last_updated_at: string; id: string }>
 resetKey={JSON.stringify({ statusFilter, appliedSearch, tableNonce })}
 pageSize={25}
 emptyMessage='No articles. Click "Seed plan" to insert the 1,000,000,000-article plan.'
 columns={[
 { label: "", className: "w-10" },
 { label: "Title" },
 { label: "Status", className: "w-24" },
 { label: "Score", className: "w-20" },
 { label: "Actions", className: "w-64" },
 ]}
 rowKey={(r: any) => r.id}
 fetchPage={async (cursor) => {
 const r: any = await callList({
 data: {
 status: statusFilter,
 search: appliedSearch || undefined,
 pageSize: 25,
 cursor,
 },
 });
 return { rows: r.rows, nextCursor: r.nextCursor };
 }}
 renderRow={(r: any) => (
 <>
 <td className="p-3"><input type="checkbox" checked={!!selected[r.id]} onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))} /></td>
 <td className="p-3">
 <Link
 to="/blog/article/$slug"
 params={{ slug: r.slug }}
 target="_blank"
 rel="noopener noreferrer"
 className="font-medium hover:text-primary hover:underline inline-flex items-center gap-1.5 group"
 >
 {r.title}
 <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
 </Link>
 <p className="text-xs text-muted-foreground">/{r.slug}</p>
 </td>
 <td className="p-3"><span className="text-xs px-2 py-0.5 bg-muted rounded-sm">{r.status}</span></td>
 <td className="p-3"><span className={r.seo_score >= 80 ? "text-emerald-600" : r.seo_score >= 50 ? "text-amber-600" : "text-muted-foreground"}>{r.seo_score}</span></td>
 <td className="p-3 space-x-1">
 <Button size="sm" variant="outline" onClick={() => setPreviewId(r.id)}><Eye className="h-3 w-3" /> Preview / Edit</Button>
 {r.status !== "published" && <Button size="sm" variant="outline" onClick={() => flip(r.id, "published")} disabled={r.seo_score < 60}>Publish</Button>}
 {r.status !== "review" && <Button size="sm" variant="ghost" onClick={() => flip(r.id, "review")}>Review</Button>}
 {r.status !== "noindex" && <Button size="sm" variant="ghost" onClick={() => flip(r.id, "noindex")}>Noindex</Button>}
 <Button size="sm" variant="ghost" onClick={() => regenMeta(r.id)}>AI meta</Button>
 </td>
 </>
 )}
 />

 </div>
 <ArticlePreviewDrawer
 articleId={previewId}
 open={previewId !== null}
 onClose={() => setPreviewId(null)}
 onSaved={reload}
 />
 </AdminPageShell>
 );
}

function Stat({ label, value }: { label: string; value: any }) {
 return (
 <div className="card-surface-flat p-4">
 <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
 <p className="text-2xl font-semibold mt-1">{value}</p>
 </div>
 );
}
